import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { formatDateWith, todayISO, type DateFormatPref } from '../../lib/date';

interface DateFormatOption {
  value: DateFormatPref;
  /** Short parenthetical hint shown after the live example. */
  hint: string;
}

// Order: US-numeric, intl-numeric, ISO, written-month (the ambiguity-proof
// default, listed last but pre-selected by the caller).
const OPTIONS: DateFormatOption[] = [
  { value: 'mdy', get hint() { return i18n.t('shared.dateFormatSelector.hints.monthFirst'); } },
  { value: 'dmy', get hint() { return i18n.t('shared.dateFormatSelector.hints.dayFirst'); } },
  { value: 'ymd', get hint() { return i18n.t('shared.dateFormatSelector.hints.iso'); } },
  { value: 'dd-mon-yyyy', get hint() { return i18n.t('shared.dateFormatSelector.hints.writtenMonth'); } },
];

interface DateFormatSelectorProps {
  value: DateFormatPref;
  onChange: (value: DateFormatPref) => void;
  /** Optional label above the control. */
  label?: string;
  disabled?: boolean;
}

/**
 * A compact radio group that shows each date-format option as a LIVE example
 * using today's date (e.g. "07/09/2026 (month first)"). Shared by the signup
 * form and Settings so the two stay identical. Renders written-month
 * ("9 Jul 2026") as the ambiguity-proof default.
 */
export const DateFormatSelector: React.FC<DateFormatSelectorProps> = ({
  value,
  onChange,
  label: labelProp,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const label = labelProp ?? t('shared.dateFormatSelector.label');
  const today = todayISO();

  return (
    <fieldset className="w-full" disabled={disabled}>
      {label && (
        <legend className="block text-sm font-medium text-charcoal mb-1.5">{label}</legend>
      )}
      <div className="grid grid-cols-2 gap-2">
        {OPTIONS.map((opt) => {
          const selected = value === opt.value;
          return (
            <label
              key={opt.value}
              className={`
                flex items-center gap-2 rounded-xl border px-3 py-2.5 cursor-pointer transition-all
                min-h-[44px]
                ${
                  selected
                    ? 'border-sage-500 bg-sage-50 shadow-glow'
                    : 'border-faint hover:border-gray-300 bg-white'
                }
              `}
            >
              <input
                type="radio"
                name="date_format"
                value={opt.value}
                checked={selected}
                onChange={() => onChange(opt.value)}
                className="accent-sage-600"
              />
              <span className="text-sm text-gray-800">
                <span className="font-medium">{formatDateWith(today, opt.value)}</span>
                <span className="text-gray-500">{' '}{t('shared.dateFormatSelector.hintInParentheses', { hint: opt.hint })}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
};

export default DateFormatSelector;
